import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Music2, Facebook, Twitter, Youtube, Instagram } from "lucide-react";
import { useTheme } from "../ThemeContext";

const Footer = ({ footer }) => {
  switch (footer) {
    case 6:
      return <Footer6 />;
    default:
      return <Footer6 />;
  }
};

const Footer6 = () => {
  const { isDarkMode } = useTheme();
  const quickLinks = [
    { label: "Home", to: "/" },
    { label: "About", to: "/about" },
    { label: "Contact", to: "/contact" },
    { label: "Privacy Policy", to: "/privacy-policy" },
    { label: "Terms of Service", to: "/terms-and-conditions" },
    { label: "Account Deletion", to: "/account-deletion" },
  ];

  const servicesLinks = [
    { label: "Asset Monitoring", to: "/service/asset-monitoring" },
    { label: "Performance Analytics", to: "/service/performance-analytics" },
    { label: "Round-The-Clock Power Optimization", to: "/service/rtc-power-optimization" },
    { label: "Energy Trading Optimization", to: "/service/energy-trading-optimization" },
    { label: "Image/ Video Analytics", to: "/service/image-video-analytics" },
    { label: "Offline Measurement", to: "/service/offline-measurement" },
    { label: "Spares/ Vendors Management", to: "/service/spares-vendors-management" },
  ];

  const socialIcons = [
    { icon: Music2, href: "#" },
    { icon: Facebook, href: "https://facebook.com/axonmetric" },
    { icon: Twitter, href: "https://twitter.com/axonmetric" },
    { icon: Youtube, href: "#" },
    { icon: Instagram, href: "https://instagram.com/axonmetric" },
  ];

  return (
    <main className="relative w-full min-h-[115vh] overflow-x-hidden flex flex-col items-center font-sans selection:bg-white/20 selection:text-white">
      <video
        className="absolute inset-0 w-full h-full object-cover z-[0]"
        autoPlay
        loop
        muted
        playsInline
        src="https://d8j0ntlcm91z4.cloudfront.net/user_38xzZboKViGWJOttwIXH07lWA1P/hf_20260429_114316_1c7889ad-2885-410e-b493-98119fee0ddb.mp4"
      />
      <div className={`absolute inset-0 z-[1] transition-colors duration-300 ${isDarkMode ? 'bg-[#0B1120]/75' : 'bg-black/35'}`}></div>

      <div className="relative z-10 w-full max-w-7xl px-4 pb-12 md:px-8 md:pb-16 flex min-h-[115vh] flex-col">
        <div className="flex flex-1 items-end justify-center">
          <div className="text-white/90 text-center">
            <h2 className="mt-2 text-2xl font-medium text-white md:text-4xl">Pure Insight Zero Noise</h2>
          </div>
        </div>

        <motion.footer
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.4, ease: "easeOut" }}
          className={`liquid-glass w-full rounded-3xl p-6 md:p-10 text-white/70 mt-32 md:mt-64 border transition-colors ${
            isDarkMode ? 'border-slate-700/60 shadow-2xl shadow-slate-950/60' : 'border-white/10'
          }`}
        >
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-12 mb-10">
            <div className="md:col-span-5">
              <div className="flex items-center gap-3 text-white">
                <img src="/Axon-logo.png" alt="AxonMetric Logo" className="h-10 w-auto" />
              </div>
              <p className="mt-5 max-w-sm text-sm leading-relaxed text-white/70">
                AxonMetric Global Private Limited (AxonMetric) is a cutting-edge technology company in the power domain committed to improving the performance of renewable energy assets.
              </p>
            </div>

            <div className="md:col-span-7 grid grid-cols-1 sm:grid-cols-3 gap-8">
              <div>
                <h4 className="text-sm uppercase tracking-wider text-white font-medium mb-4">Quick Links</h4>
                <ul className="text-xs space-y-2">
                  {quickLinks.map((item) => (
                    <li key={item.to}>
                      <Link to={item.to} className="hover:text-white transition-colors">
                        {item.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h4 className="text-sm uppercase tracking-wider text-white font-medium mb-4">Services</h4>
                <ul className="text-xs space-y-2">
                  {servicesLinks.map((item) => (
                    <li key={item.to}>
                      <Link to={item.to} className="hover:text-white transition-colors">
                        {item.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h4 className="text-sm uppercase tracking-wider text-white font-medium mb-4">Contact Info</h4>
                <ul className="text-xs space-y-2">
                  <li>India</li>
                  <li>
                    <a href="tel:+91-7827355185" className="hover:text-white transition-colors">
                      +91-7827355185
                    </a>
                  </li>
                  <li>
                    <a href="mailto:info@axonmetric.com" className="hover:text-white transition-colors">
                      info@axonmetric.com
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="pt-6 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-6 md:gap-4">
            <div className="flex flex-col sm:flex-row items-center gap-3 md:gap-6 text-center sm:text-left">
              <p className="text-[10px] uppercase tracking-widest opacity-50">© 2026 AxonMetric Global Private Limited. All rights reserved.</p>
              <div className="flex items-center gap-3 text-[11px] text-white/60">
                <Link to="/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</Link>
                <span>•</span>
                <Link to="/terms-and-conditions" className="hover:text-white transition-colors">Terms of Service</Link>
                <span>•</span>
                <Link to="/account-deletion" className="hover:text-white transition-colors">Account Deletion</Link>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-[10px] uppercase tracking-widest opacity-50">Connect:</span>
              <div className="flex items-center gap-3">
                {socialIcons.map((item, index) => {
                  const Icon = item.icon;

                  return (
                  <a
                    key={`social-${index}`}
                    href={item.href}
                    target={item.href.startsWith("http") ? "_blank" : undefined}
                    rel={item.href.startsWith("http") ? "noopener noreferrer" : undefined}
                    className="opacity-70 hover:opacity-100 transition-colors hover:text-white"
                    aria-label="Social link"
                  >
                    <Icon size={16} />
                  </a>
                  );
                })}
              </div>
            </div>
          </div>
        </motion.footer>
      </div>
    </main>
  );
};

export default Footer;
